import React from 'react'
import { Button, Modal, Tabs, Typography } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import {
  ECampaignChannel,
  IBroadcastPreview,
  ISendBroadcast
} from 'models/notifications'
import styles from '../Notifications.module.scss'
import {
  channelNames,
  getLinkLabel,
  getSmsSegments,
  smsCount
} from './broadcast'

const BOT_FRAME_STYLE =
  'body{margin:0;padding:16px;font:15px/1.45 -apple-system,Segoe UI,Roboto,sans-serif;color:#1f1f1f;background:#eef3f8}' +
  '.message{max-width:440px;padding:10px 14px;border-radius:12px;background:#fff;white-space:pre-wrap;word-wrap:break-word}' +
  'a{color:#2f6fed}'

export interface IPreviewState {
  payload: ISendBroadcast
  preview: IBroadcastPreview
}

interface BroadcastPreviewModalProps {
  state: IPreviewState | null
  onClose: () => void
  onShowPopup: () => void
}

export const BroadcastPreviewModal: React.FC<BroadcastPreviewModalProps> = ({
  state,
  onClose,
  onShowPopup
}) => {
  if (!state) return null
  const { payload, preview } = state
  const sellers = payload.sellers ?? {}
  const users = payload.users ?? {}
  const smsText = payload.sms_text ?? ''

  const tabs = [
    {
      key: ECampaignChannel.EMAIL,
      selected: Boolean(sellers.email || users.email),
      children: (
        <>
          <Typography.Paragraph className={styles.previewSubject}>
            Тема: <strong>{preview.email_subject}</strong>
          </Typography.Paragraph>
          <iframe
            title='Письмо'
            sandbox=''
            srcDoc={preview.email_html}
            className={styles.previewFrame}
          />
          <Typography.Text type='secondary' className={styles.previewNote}>
            Внизу письма — ссылка «Отписаться от рассылки»: она снимает согласие
            на рассылки.
          </Typography.Text>
        </>
      )
    },
    {
      key: ECampaignChannel.BOT,
      selected: Boolean(sellers.bot),
      children: (
        <iframe
          title='Seller-бот'
          sandbox=''
          srcDoc={`<style>${BOT_FRAME_STYLE}</style><div class="message">${preview.bot_message}</div>`}
          className={styles.previewBotFrame}
        />
      )
    },
    {
      key: ECampaignChannel.PUSH,
      selected: Boolean(sellers.push || users.push),
      children: (
        <div className={styles.previewPush}>
          <Typography.Text strong>{payload.title}</Typography.Text>
          <Typography.Paragraph
            ellipsis={{ rows: 4 }}
            className={styles.previewPushBody}
          >
            {payload.body}
          </Typography.Paragraph>
          {payload.data && (
            <Typography.Text type='secondary'>
              По нажатию: {getLinkLabel(payload.data)}
            </Typography.Text>
          )}
        </div>
      )
    },
    {
      key: ECampaignChannel.SMS,
      selected: Boolean(sellers.sms),
      children: (
        <>
          <div className={styles.previewSms}>
            {smsText || 'Текст SMS не заполнен'}
          </div>
          {smsText && (
            <Typography.Text type='secondary'>
              {smsText.length} символов — {smsCount(getSmsSegments(smsText))} на
              номер
            </Typography.Text>
          )}
        </>
      )
    },
    {
      key: ECampaignChannel.POPUP,
      selected: Boolean(sellers.popup),
      children: (
        <div className={styles.previewPopup}>
          <Typography.Paragraph>
            {preview.site_url
              ? `Кнопка «${
                  sellers.popup_button_text || preview.link_label
                }» откроет ${preview.site_url}`
              : 'Без ссылки окно показывается только с кнопкой «OK».'}
          </Typography.Paragraph>
          <Button icon={<EyeOutlined />} onClick={onShowPopup}>
            Показать окно
          </Button>
        </div>
      )
    }
  ]
  const selected = tabs.filter((tab) => tab.selected)

  return (
    <Modal
      open
      title='Предпросмотр'
      width={760}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Закрыть</Button>}
    >
      {preview.site_url && (
        <Typography.Paragraph type='secondary' className={styles.summaryLink}>
          Ссылка в письме, боте и окне: {preview.site_url}
        </Typography.Paragraph>
      )}
      <Tabs
        items={(selected.length ? selected : tabs).map((tab) => ({
          key: tab.key,
          label: channelNames[tab.key],
          children: tab.children
        }))}
      />
    </Modal>
  )
}
